"use client"

import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import GameCard from "@/stories/molecules/cards/gameCard/GameCard"

import { filteredGamesPageSize, visibleFilteredCount } from "../data/lobby"
import {
  categoryGamesPreviewTexts,
  formatFilteredDisplaying,
  lobbyTexts
} from "../localization/texts"
import type { PreviewGame } from "./GamesPreviewRow"

export type GamesFilteredGridProps = {
  games: PreviewGame[]
  categorySelected: string
}

export function GamesFilteredGrid({
  games,
  categorySelected
}: GamesFilteredGridProps) {
  const [page, setPage] = useState(1)
  const shown = visibleFilteredCount(page, games.length)
  const visibleGames = games.slice(0, shown)
  const hasMore = shown < games.length
  const showLoadMore = games.length > filteredGamesPageSize
  const progressValue = games.length === 0 ? 0 : (shown / games.length) * 100
  const displaying = formatFilteredDisplaying(shown, games.length)

  if (games.length === 0) {
    return (
      <p className="px-2 py-8 text-center text-sm text-white/50">
        {categoryGamesPreviewTexts.empty}
      </p>
    )
  }

  return (
    <section aria-label={lobbyTexts.ariaLabel}>
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-3 gap-2 md:grid-cols-4 xlg:grid-cols-6 xlg:gap-4">
          {visibleGames.map((game, index) => (
            <GameCard
              key={game.identifier}
              game={game}
              index={index}
              priority={index < 6}
              categorySelected={categorySelected}
              showFavoriteButton
              showCurrencyBadge={false}
              showPlayButton
            />
          ))}
        </div>

        {showLoadMore ? (
          <div className="flex flex-col items-center gap-3">
            <Progress
              value={progressValue}
              className="h-[12px] w-[176px] rounded-[1000px] bg-black-600"
              indicatorClassName="rounded-[1000px] bg-purple-500"
              aria-label={displaying}
            />
            <p className="text-center text-sm text-light-text text-white">{displaying}</p>
            {hasMore ? (
              <Button
                type="button"
                variant="muted"
                className="h-[52px] w-[157px]"
                onClick={() => {
                  setPage((current) => current + 1)
                }}
              >
                {lobbyTexts.filtered.loadMore}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  )
}
